import moment from "moment"
import {formatDateOnly} from "helpers"

export const ATTENDANCE_METRICS = [
    {key: "scheduleHours", label: "Scheduled"},
    {key: "attendedHours", label: "Attended"},
    {key: "missedHours", label: "Missed"},
    {key: "makeUpHours", label: "Make-up"},
    {key: "distEdHours", label: "DistEd"}
]

export function getMonthsInRange(startDate: any, endDate: any) {
    const start = moment(startDate).startOf("month")
    const end = moment(endDate).startOf("month")
    const months = []
    let current = start.clone()
    while (current.isSameOrBefore(end)) {
        months.push(current.clone())
        current.add(1, "month")
    }
    return months
}

export function buildMonthWeeks(momentMonth: any) {
    const daysInMonth = momentMonth.daysInMonth()
    const startWeekday = momentMonth.startOf("month").day()
    const weeks = []
    let currentWeek = Array(startWeekday).fill(null)
    for (let d = 1; d <= daysInMonth; d++) {
        currentWeek.push(d)
        if (currentWeek.length === 7) {
            weeks.push(currentWeek)
            currentWeek = []
        }
    }
    if (currentWeek.length > 0) {
        while (currentWeek.length < 7) currentWeek.push(null)
        weeks.push(currentWeek)
    }
    return weeks
}

/** YYYY-MM-DD key — Moment keeps calendar day; API ISO uses helpers.toDateOnly. */
export function formatDateKey(date: any) {
    if (!date) return ""
    if (moment.isMoment(date)) return date.format("YYYY-MM-DD")
    return formatDateOnly(date, "YYYY-MM-DD")
}

export type AttendanceHourTotals = {
    totalScheduleHours: number
    totalScheduleHoursToLda: number
    totalAttendedHours: number
    totalMissedHours: number
    totalMakeUpHours: number
    totalDistEdHours: number
}

/** Last calendar day with attended hours > 0 (LDA proxy from daily rows). */
export function getLastDayOfAttendanceKey(dailyAttendances: any[] = []): string | null {
    let ldaKey: string | null = null
    for (const item of dailyAttendances) {
        if (Number(item?.attendedHours ?? 0) <= 0) continue
        const dateKey = formatDateKey(item.date)
        if (!dateKey) continue
        if (!ldaKey || dateKey > ldaKey) ldaKey = dateKey
    }
    return ldaKey
}

export function aggregateAttendanceHourTotals(
    dailyAttendances: any[] = [],
    options: {
        year?: number
        month?: number
        fromDate?: string
        toDate?: string
        ldaDateKey?: string | null
    } = {}
): AttendanceHourTotals {
    const {year, month, fromDate, toDate, ldaDateKey} = options
    const totals: AttendanceHourTotals = {
        totalScheduleHours: 0,
        totalScheduleHoursToLda: 0,
        totalAttendedHours: 0,
        totalMissedHours: 0,
        totalMakeUpHours: 0,
        totalDistEdHours: 0
    }

    for (const item of dailyAttendances) {
        const dateKey = formatDateKey(item?.date)
        if (!dateKey) continue
        if (fromDate && dateKey < fromDate) continue
        if (toDate && dateKey > toDate) continue

        if (year !== undefined || month !== undefined) {
            const dateMoment = moment(dateKey, "YYYY-MM-DD")
            if (year !== undefined && dateMoment.year() !== year) continue
            if (month !== undefined && dateMoment.month() !== month) continue
        }

        const isLoa = item?.scheduleHours === "LOA"
        const scheduleHours = isLoa ? 0 : Number(item?.scheduleHours ?? 0)
        const attendedHours = Number(item?.attendedHours ?? 0)
        const missedHours = Number(item?.missedHours ?? 0)
        const makeUpHours = Number(item?.makeUpHours ?? 0)
        const distEdHours = Number(item?.distEdHours ?? 0)

        totals.totalScheduleHours += scheduleHours
        totals.totalAttendedHours += attendedHours
        totals.totalMissedHours += missedHours
        totals.totalMakeUpHours += makeUpHours
        totals.totalDistEdHours += distEdHours

        if (!ldaDateKey || dateKey <= ldaDateKey) {
            totals.totalScheduleHoursToLda += scheduleHours
        }
    }

    return totals
}

export type MonthlySummaryItem = {
    month: string
    cumulativeAttendedHours?: number
    cumulativeMissedHours?: number
    cumulativeScheduledHours?: number
    cumulativeMakeupHours?: number
    cumulativeDistedHours?: number
}

export type AttendancePriorTotals = {
    totalScheduleHours: number
    totalScheduleHoursToLda?: number
    totalAttendedHours: number
    totalMissedHours: number
    totalMakeUpHours: number
    totalDistEdHours: number
}

/** Map API monthlySummary[] by "YYYY-MM". */
export function buildMonthlySummaryByKey(monthlySummary: MonthlySummaryItem[] = []) {
    const map: Record<string, MonthlySummaryItem> = {}
    for (const item of monthlySummary) {
        if (!item?.month) continue
        map[item.month] = item
    }
    return map
}

/**
 * Latest monthlySummary entry strictly before monthKey.
 * API only emits months with activity (sparse) and cumulative* includes that month —
 * so Prior for M = cumulative of last entry with month < M.
 */
export function getLatestMonthlySummaryBefore(
    monthlySummaryByKey: Record<string, MonthlySummaryItem>,
    monthKey: string
): MonthlySummaryItem | null {
    let best: MonthlySummaryItem | null = null
    for (const item of Object.values(monthlySummaryByKey)) {
        if (!item?.month || item.month >= monthKey) continue
        if (!best || item.month > best.month) best = item
    }
    return best
}

/** Prior totals for a calendar month from API monthlySummary entry. */
export function getPriorTotalsFromMonthlySummary(summary?: MonthlySummaryItem | null): AttendancePriorTotals {
    return {
        totalScheduleHours: Number(summary?.cumulativeScheduledHours ?? 0),
        totalAttendedHours: Number(summary?.cumulativeAttendedHours ?? 0),
        totalMissedHours: Number(summary?.cumulativeMissedHours ?? 0),
        totalMakeUpHours: Number(summary?.cumulativeMakeupHours ?? 0),
        totalDistEdHours: Number(summary?.cumulativeDistedHours ?? 0)
    }
}

/** Prior totals for monthKey — carry-forward across sparse/empty months. */
export function getPriorTotalsForMonth(
    monthlySummaryByKey: Record<string, MonthlySummaryItem>,
    monthKey: string
): AttendancePriorTotals {
    return getPriorTotalsFromMonthlySummary(getLatestMonthlySummaryBefore(monthlySummaryByKey, monthKey))
}
